import React, { useEffect, useRef, useState } from "react";
import useKeyPress from "../hooks/useKeyPress";
import ProblemStatement from "./ProblemStatement";
import Popup from "./Popup";
import { defineTheme } from "../lib/defineTheme";
import { GC, MESSAGE_DATA_TYPE } from "../services/config/gamecentreService";

import "react-toastify/dist/ReactToastify.css";
import { defaultCodingTheme } from "../constants/monacoThemes";
import CodeWindow from "./CodeWindow";
import { COMPONENT_TYPE, POPUP_TYPE } from "../constants/common";
import DetailsWindow from "./DetailsWindow";

const javascriptDefault = `/**
* Code here
*/
`;

const Landing = (props) => {
  const { gameData, gcLoaded, processing, timer,
    setProcessing, sendGameDataSaveMessageWithoutAuth } = props;

  const [code, setCode] = useState(javascriptDefault);
  const [testCaseInput, setTestCaseInput] = useState([]);
  const [outputDetails, setOutputDetails] = useState(null);
  const [isCompiling, setIsCompiling] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(null);
  const [theme, setTheme] = useState(defaultCodingTheme);
  const [language, setLanguage] = useState(null);
  const [allLanguages, setAllLanguages] = useState([]);
  const [selectedProblem, setSelectedProblem] = useState(null);
  const enterPress = useKeyPress("Enter");
  const ctrlPress = useKeyPress("Control");
  const [openPopup, setOpenPopup] = useState(false);

  // Added code tracking across language switch
  const trackCurrentLanguage = useRef(null);
  const trackDriverCodes = useRef({});

  useEffect(() => {
    if (gameData && !gameData.outputDetails) {
      const { problem, languages } = gameData;
      if (problem) {
        const problemOpts = [problem].map((prob) => ({
          id: prob?._id,
          label: prob?.title,
          ...prob,
        }));
        setSelectedProblem(problemOpts?.[0]);
      }
      if (languages && languages.length) {
        const langs = languages
          ?.map(({ _id, name, code, value }) => ({
            _id,
            name,
            code,
            label: name,
            value,
          }))
          .filter(({ status }) => status !== "active");
        setAllLanguages(langs);
        let language
        if (gameData?.languageId) {
          language = langs.find((lang) => lang._id === gameData.languageId)
        } else {
          language = langs?.[0]
        }
        // Setting state/ref for language 
        // trackCurrentLanguage.current = null causes issues in lang switch
        setLanguage(language)
        trackCurrentLanguage.current = language;
        if (gameData?.code) {
          setCode(gameData.code)
        }
      }
    }
    if (gameData && gameData.outputDetails) {
      setOutputDetails(gameData.outputDetails);
    }
  }, [gameData]);

  useEffect(() => {
    if (selectedProblem) {
      setTestCaseInput(selectedProblem?.testcases?.filter(testcase => testcase?.compile));
    }
  }, [selectedProblem]);

  useEffect(() => {
    if (!processing) {
      setIsCompiling(false);
      setIsSubmitting(false);
    }
  }, [processing]);

  useEffect(() => {
    const { gameData: { driverCodes } } = props;
    if (driverCodes && driverCodes.length) {
      const findDriverCode = driverCodes.find((
        elem => elem?.languageId === language?._id));
      if (findDriverCode) {
        if (code === javascriptDefault) {
          setCode(findDriverCode?.code);
          trackCurrentLanguage.current = language;
          return;
        }
        // Handle code retention by user on language switch
        const driverCodeTracker = trackDriverCodes.current;
        const languageTracker = trackCurrentLanguage.current;
        driverCodeTracker[languageTracker._id] = code;
        // Set code driverCodeTracker if langId already in tracker
        if (driverCodeTracker[language._id]) {
          setCode(driverCodeTracker[language._id]);
        } else {
          setCode(findDriverCode?.code);
        }
        trackCurrentLanguage.current = language;
      } else {
        setCode(javascriptDefault);
      }
    }
  }, [selectedProblem, language]);

  useEffect(() => {
    if (enterPress && ctrlPress) {
      handleCompile();
    }
  }, [ctrlPress, enterPress]);

  useEffect(() => {
    defineTheme("brilliance-black").then((_) =>
      setTheme(defaultCodingTheme)
    );
  }, []);

  // When code not submitted before timer expires, make call to BE -> Score calculation
  useEffect(() => {
    if (timer < 1) handleSubmit();
    if (timer && timer % 60 === 0) {
      if (!language?._id || !code) return;
      const sessionPayload = {
        startTime: new Date().toISOString(),
        code: code,
        languageId: language._id
      };
      GC.sendGameDataSaveMessage(sessionPayload, MESSAGE_DATA_TYPE.CODE_SESSION);
    }
  }, [timer]);

  const onLanguageChange = (sl) => {
    setLanguage(sl);
  };

  const executeCode = (action = "compile") => {
    if (action === "compile") {
      setIsCompiling(true);
    }
    if (action === "submit") {
      setIsSubmitting(true);
      setOpenPopup(false);
    }
    if (outputDetails) setOutputDetails(null);
    const { CODE_COMPILE, CODE_SUBMIT } = MESSAGE_DATA_TYPE;
    const formData = {
      languageCode: language?.code,
      sourceCode: code,
    };
    const saveCallType = action === "compile" ? CODE_COMPILE : CODE_SUBMIT;
    if (!gcLoaded) {
      sendGameDataSaveMessageWithoutAuth(formData, saveCallType);
    } else {
      setProcessing(true);
      GC.sendGameDataSaveMessage(formData, saveCallType);
    }
  };

  const handleSubmit = () => executeCode("submit");
  const handleCompile = () => executeCode("compile");

  return (
    <div className="">
      <div className="flex h-[100vh] w-[100%] justify-between">
        <ProblemStatement
          selectedProblem={selectedProblem}
        />
        <div className="section-separator"></div>
        <div className="flex flex-col w-[65%] items-start">
          {/* Extract in separate component */}
          <CodeWindow
            onLanguageChange={onLanguageChange}
            allLanguages={allLanguages}
            language={language}
            timer={timer}
            handleCompile={handleCompile}
            setOpenPopup={setOpenPopup}
            setCode={setCode}
            theme={theme}
            code={code}
            isCompiling={isCompiling}
            isSubmitting={isSubmitting}
          />
          <div className="w-full flex grow">
            {
              Object.keys(COMPONENT_TYPE).map((elem, index) => {
                return (
                  <>
                    <DetailsWindow
                      outputs={outputDetails}
                      type={elem}
                      testCaseInput={testCaseInput}
                      setTestCaseInput={setTestCaseInput}
                    />
                    {index === 0 && <div className="section-separator"></div>}
                  </>
                );
              })
            }
          </div>
        </div>
      </div>
      <Popup
        message={!code ? 'You are submitting an empty answer.': ''}
        screen={POPUP_TYPE.SUBMIT}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        handleSubmit={handleSubmit}
      />
    </div>
  );
};
export default Landing;
